"use client";

import { useEffect } from "react";
import { CONSENT_EVENT, STORAGE_KEY } from "@/components/cookies/constants";

/**
 * Microsoft Clarity (session recordings and heatmaps)
 *
 * Loaded outside GTM so it can follow the cookie banner directly:
 * - Nothing loads until the visitor grants analytics consent.
 * - Consent is passed to Clarity with its Consent API v2 on load and on every change;
 *   a later "deny" makes Clarity drop its cookies.
 * - Pages with customer data (dashboard, reports, chat, checkout success) carry
 *   data-clarity-mask="true" on their root, so their content never reaches recordings.
 *
 * The project ID is public (it ships in the page either way), so it lives here
 * rather than in an env var that would need a build arg in Dokploy.
 */
const CLARITY_PROJECT_ID = "yrrqmv7538";

interface StoredConsent {
  analytics?: boolean;
  marketing?: boolean;
}

function readStoredConsent(): StoredConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as StoredConsent) : null;
  } catch {
    return null;
  }
}

function loadClarity(): void {
  if (typeof window.clarity === "function") return;

  // Official snippet: queue calls until the tag script arrives
  const queued = function (...args: unknown[]) {
    (queued.q = queued.q || []).push(args);
  } as ClarityFn;
  window.clarity = queued;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
}

function applyConsent(consent: StoredConsent): void {
  if (consent.analytics) {
    loadClarity();
  }
  if (typeof window.clarity !== "function") return;

  window.clarity("consentv2", {
    ad_Storage: consent.marketing ? "granted" : "denied",
    analytics_Storage: consent.analytics ? "granted" : "denied",
  });
}

export function MicrosoftClarity() {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    const stored = readStoredConsent();
    if (stored) applyConsent(stored);

    const onChange = (event: Event) => {
      applyConsent((event as CustomEvent<StoredConsent>).detail);
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  return null;
}

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}
